import { Check, Save, Share2, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { endpoints } from '../../services/api';
import { assignmentFor, useScreen } from '../../store/screen';
import { useSession } from '../../store/session';
import { ShareLayoutForm } from './SharingTab';
import { applyPreset, deletePreset, overwritePreset, savePreset, type Preset } from '../../store/presets';
import { LAYOUTS, THEMES, useSettings } from '../../store/settings';
import { buttonClass, ghostButtonClass, inputClass, Section } from './controls';

function summary(p: Preset): string {
  const s = p.snapshot ?? p.look;
  if (!s) return '';
  const parts = [LAYOUTS.find((l) => l.id === s.layout)?.name, THEMES.find((t) => t.id === s.theme)?.name];
  if (p.snapshot) {
    const photos = p.snapshot.media.cloudFolders.length + p.snapshot.media.items.length;
    parts.push(photos ? `${photos} photo source${photos === 1 ? '' : 's'}` : 'no photos');
  } else parts.push('style only');
  return parts.filter(Boolean).join(' · ');
}

/** Save the current setup under a name, and switch between saved setups. */
export function PresetsSection() {
  const presets = useSettings((s) => s.presets);
  const [name, setName] = useState('');
  const [applied, setApplied] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [sharing, setSharing] = useState<string | null>(null);
  const signedIn = useSession((s) => Boolean(s.user) && Boolean(s.config?.features.accounts));

  const save = (e: FormEvent) => {
    e.preventDefault();
    const p = savePreset(name || `Layout ${presets.length + 1}`);
    setName('');
    setApplied(p.id);
  };

  return (
    <Section
      title="Saved layouts"
      description="Save this screen’s whole setup – layout, theme and size, the photos and folders, calendars, news feeds, weather location and every widget option – and switch back any time. Files added from a device stay on that screen only."
    >
      <form onSubmit={save} className="flex gap-2">
        <input
          className={inputClass}
          placeholder="Name, e.g. Kitchen tablet or Evening"
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          aria-label="Layout name"
        />
        <button className={buttonClass} type="submit">
          <Save className="h-4 w-4" aria-hidden /> Save
        </button>
      </form>
      {presets.length > 0 && (
        <ul className="space-y-2">
          {presets.map((p) => (
            <li key={p.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-[var(--border)] px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{p.name}</div>
                <div className="muted truncate text-xs">
                  {summary(p)}
                </div>
              </div>
              <button
                className={buttonClass}
                onClick={() => {
                  // A screen showing a saved layout switches to this one; others take it as their setup.
                  const user = useSession.getState().user;
                  if (user && assignmentFor(user.id)) {
                    useScreen.getState().setAssignment(user.id, p.id);
                    void endpoints.updateScreen(useScreen.getState().deviceId, { presetId: p.id }).catch(() => undefined);
                  } else applyPreset(p);
                  setApplied(p.id);
                }}
              >
                {applied === p.id ? <Check className="h-4 w-4" aria-hidden /> : null} Apply
              </button>
              <button className={ghostButtonClass} onClick={() => overwritePreset(p.id)} title="Replace with the current look">
                Update
              </button>
              {signedIn && (
                <button className={ghostButtonClass} onClick={() => setSharing(sharing === p.id ? null : p.id)} aria-label={`Share ${p.name}`}>
                  <Share2 className="h-4 w-4" aria-hidden /> Share
                </button>
              )}
              {confirmDelete === p.id ? (
                <button
                  className={`${ghostButtonClass} border-red-400 text-red-400`}
                  onClick={() => {
                    deletePreset(p.id);
                    setConfirmDelete(null);
                  }}
                >
                  Delete?
                </button>
              ) : (
                <button className={ghostButtonClass} onClick={() => setConfirmDelete(p.id)} aria-label={`Delete ${p.name}`}>
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              )}
              {sharing === p.id && (
                <div className="w-full">
                  <ShareLayoutForm preset={p} onDone={() => setSharing(null)} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="muted text-xs">Saved layouts sync to every screen signed in to your account.</p>
    </Section>
  );
}
